import { Workbox } from 'workbox-window'

/**
 * The only module that talks to the service-worker registration, and the only
 * module in the codebase permitted to mention `skipWaiting` (D-044 rule 2).
 * `scripts/check-boundaries.mjs` fails the build if the word appears anywhere
 * else, so the "exactly one place" claim is enforced rather than asserted.
 *
 * The service worker itself never calls `skipWaiting` in its `install` handler:
 * `vite.config.ts` uses `registerType: 'prompt'` with Workbox's `skipWaiting:
 * false`, so a new worker installs and then waits, indefinitely, until this
 * module messages it. A regression test reads the generated `dist/sw.js` and
 * asserts that.
 *
 * The service worker never touches IndexedDB. It precaches the versioned shell
 * and nothing else; user data lives in IndexedDB, which no cache participates
 * in (docs/privacy-and-storage.md).
 */

export interface ServiceWorkerClient {
  /** Activate the waiting worker. Guarded by the caller; see `activate`. */
  readonly activate: () => void
  /** Unregister listeners. Used by tests. */
  readonly dispose: () => void
}

export interface ServiceWorkerClientOptions {
  /** Called when a new worker has installed and is waiting. */
  readonly onWaiting: () => void
  /**
   * Called when the new worker takes control. The app reloads here; by this
   * point the draft save has already been confirmed to have resolved.
   */
  readonly onControlling: () => void
}

/**
 * Register the service worker and report update lifecycle events.
 *
 * Returns `null` when service workers are unavailable — an unsupported browser,
 * a private context, or an insecure origin. Every caller treats that as "no
 * updates to offer", never as an error: the app is fully functional without a
 * service worker, it simply is not installable or offline-capable.
 */
export function createServiceWorkerClient(
  options: ServiceWorkerClientOptions,
): ServiceWorkerClient | null {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null

  /* BASE_URL honours the relative `base` in vite.config.ts, so this resolves
     correctly when the app is hosted from a subpath. */
  const workbox = new Workbox(`${import.meta.env.BASE_URL}sw.js`)

  const handleWaiting = () => {
    options.onWaiting()
  }
  const handleControlling = () => {
    options.onControlling()
  }

  workbox.addEventListener('waiting', handleWaiting)
  workbox.addEventListener('controlling', handleControlling)

  void workbox.register()

  return {
    activate: () => {
      // The single call site. Reaching it requires `mayActivateWaitingWorker`,
      // which is true only in the `applying` status — see updateMachine.ts.
      workbox.messageSkipWaiting()
    },
    dispose: () => {
      workbox.removeEventListener('waiting', handleWaiting)
      workbox.removeEventListener('controlling', handleControlling)
    },
  }
}
